import Toaster from "@/components/toaster";
import Protected from "@/pages/protected";
import { DataService } from "@/services/data";
import { PaginatedResponse } from "@/types/common";
import { ProfessionDisplayType } from "@/types/content/registration/profession";
import { ProspectingMail } from "@/types/tools/prospecting_mail";
import { handle_error, handle_success } from "@/utils/wretch";
import { Navigate, Outlet, RouteObject, redirect } from "react-router-dom";
import { toast } from "sonner";

const MailingCampaignRoute: RouteObject = {
    path: "campagnes-postales",
    element: (
        <Protected groups={["app_tools_admin", "app_tools_mailing_campaign"]}>
            <Outlet />
        </Protected>
    ),
    action: async ({ request }) => {
        const data = await request.json();
        const url = new URL(request.url);
        const action = url.searchParams.get("action");
        try {
            return handle_success(
                await DataService.client
                    .url(`/tools/prospecting_mail/${action}/${url.searchParams ? "?" + url.searchParams.toString() : ""}`)
                    .post(data)
                    .json()
            );
        } catch (e) {
            return handle_error(e);
        }
    },
    children: [
        {
            index: true,
            async lazy() {
                let { MailingCampaignList } = await import("@/pages/tools/mailing_campaign/list");
                return { Component: MailingCampaignList };
            },
            loader: async ({ request }) => {
                const url = new URL(request.url);
                if (!url.searchParams.has("page_size")) {
                    url.searchParams.set("page_size", "25");
                }
                const prospecting_mails = (await DataService.client
                    .url(`/tools/prospecting_mail/${url.searchParams ? "?" + url.searchParams.toString() : ""}`)
                    .get()
                    .json()) as PaginatedResponse<ProspectingMail>;
                return { prospecting_mails: prospecting_mails };
            }
        },
        {
            path: "nouveau",
            action: async ({ request }) => {
                const data = await request.json();
                try {
                    const instance = (await DataService.client.url(`/tools/prospecting_mail/`).post(data).json()) as ProspectingMail;
                    toast.success("La campagne a été créée");
                    return redirect(`/outils/campagnes-postales/${instance.id}`);
                } catch (e) {
                    return handle_error(e);
                }
            }
        },
        {
            path: "picklists",
            children: [
                {
                    path: "professions",
                    loader: async () => {
                        const professions = (await DataService.client.url(`/tools/prospecting_mail/professions/`).get().json()) as ProfessionDisplayType[];
                        return professions;
                    }
                },
                {
                    path: "campaigns",
                    loader: async () => {
                        const campaigns = (await DataService.client.url(`/tools/prospecting_mail/campaigns/`).get().json()) as { id: number; name: string }[];
                        return campaigns;
                    }
                },
                {
                    path: "users",
                    loader: async () => {
                        const users = (await DataService.client.url(`/tools/prospecting_mail/users/`).get().json()) as { id: number; name: string }[];
                        return users;
                    }
                }
            ]
        },
        {
            id: "mailing_campaign_detail",
            path: ":pm_id",
            async lazy() {
                let { DetailLayout } = await import("@/pages/tools/mailing_campaign/detail-layout");
                return { Component: DetailLayout };
            },
            loader: async ({ params }) => {
                const prospecting_mail = (await DataService.client.url(`/tools/prospecting_mail/${params.pm_id}/`).get().json()) as ProspectingMail;
                return { prospecting_mail: prospecting_mail };
            },
            action: async ({ params, request }) => {
                const url = new URL(request.url);
                const action = url.searchParams.get("action");
                const body = await request.json();
                try {
                    if (action) {
                        return handle_success(await DataService.client.url(`/tools/prospecting_mail/${params.pm_id}/${action}/`).post(body).json());
                    } else {
                        return handle_success(await DataService.client.url(`/tools/prospecting_mail/${params.pm_id}/`).patch(body).json());
                    }
                } catch (error) {
                    Toaster.error(`Une erreur est survenue`);
                    return handle_error(error);
                }
            },
            children: [
                {
                    index: true,
                    element: <Navigate to="configuration" replace />
                },
                {
                    path: "raw_leads_ids",
                    loader: async ({ params }) => {
                        const raw_leads_ids = (await DataService.client.url(`/tools/prospecting_mail/${params.pm_id}/raw_leads_ids/`).get().json()) as { raw_leads_ids: string };
                        return { success: true, content: raw_leads_ids };
                    }
                },
                {
                    path: "preview",
                    loader: async ({ params }) => {
                        const preview = (await DataService.client.url(`/tools/prospecting_mail/${params.pm_id}/preview/`).post().json()) as { leads: number };
                        return { success: true, content: preview };
                    }
                },
                {
                    path: "configuration",
                    async lazy() {
                        let { ConfigurationPage } = await import("@/pages/tools/mailing_campaign/detail-configuration");
                        return { Component: ConfigurationPage };
                    },
                    action: async ({ params, request }) => {
                        const registrationId = params.registrationId;
                        const data = await request.json();
                        try {
                            await DataService.client.url(`/apprenticeship/registrations/${registrationId}/`).patch(data).json();
                        } catch (error) {
                            console.error(error);
                            toast.error("Une erreur est survenue lors de l'enregistrement du dossier");
                        }
                        return redirect("..");
                    }
                },
                {
                    path: "destinataires",
                    async lazy() {
                        let { RecipientsPage } = await import("@/pages/tools/mailing_campaign/detail-recipients");
                        return { Component: RecipientsPage };
                    },
                    loader: async ({ params, request }) => {
                        const url = new URL(request.url);
                        url.searchParams.set("mail", params.pm_id || "");
                        if (!url.searchParams.has("page_size")) {
                            url.searchParams.set("page_size", "25");
                        }
                        const recipients = await DataService.client
                            .url(`/tools/prospecting_mail_recipient/${url.searchParams ? "?" + url.searchParams.toString() : ""}`)
                            .get()
                            .json();
                        return { recipients };
                    }
                },
                {
                    path: "performance",
                    async lazy() {
                        let { PerformancePage } = await import("@/pages/tools/mailing_campaign/detail-performance");
                        return { Component: PerformancePage };
                    },
                    loader: async ({ params }) => {
                        const performance = await DataService.client.url(`/tools/prospecting_mail/${params.pm_id}/performance/`).get().json();
                        return { performance };
                    }
                },
                {
                    path: "performance/sf_cases",
                    loader: async ({ params }) => {
                        return await DataService.client.url(`/tools/prospecting_mail/${params.pm_id}/sf_cases/`).get().json();
                    }
                }
            ]
        },
        {
            path: "performance",
            loader: async ({ request }) => {
                const url = new URL(request.url);
                try {
                    return handle_success(
                        await DataService.client
                            .url(`/tools/prospecting_mail/global_performance/${url.searchParams ? "?" + url.searchParams.toString() : ""}`)
                            .get()
                            .json()
                    );
                } catch (e) {
                    return handle_error(e);
                }
            }
        },
        {
            path: "base-de-donnees",
            async lazy() {
                let { DatabaseOverview } = await import("@/pages/tools/mailing_campaign/database-overview");
                return { Component: DatabaseOverview };
            },
            loader: async () => {
                const database_overview = DataService.client.url(`/tools/prospecting_mail/database_overview/`).get().json();
                return { database_overview };
            }
        }
    ]
};
export default MailingCampaignRoute;
